import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Navbar from './components/Navbar';
import Login from './components/auth/Login';
import LicenseRequest from './components/auth/LicenseRequest';
import ForgotPassword from './components/auth/ForgotPassword';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/admin/AdminDashboard';
import ProtectedRoute from './components/ProtectedRoute';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentCancel from './components/PaymentCancel';
import PaymentCompleteDesktop from './components/PaymentCompleteDesktop';
import CreditUsage from './components/dashboard/CreditUsage';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const AppContent = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-purple to-brand-red flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  const isAdmin = user?.is_admin || user?.id === 'admin';

  return (
    <Elements stripe={stripePromise}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
        <Router>
          {user && <Navbar />}
          <Routes>
            <Route 
              path="/login" 
              element={user ? <Navigate to={isAdmin ? "/admin" : "/dashboard"} /> : <Login />} 
            />
            <Route 
              path="/request-license" 
              element={user ? <Navigate to={isAdmin ? "/admin" : "/dashboard"} /> : <LicenseRequest />} 
            />
            <Route 
              path="/forgot-password" 
              element={user ? <Navigate to={isAdmin ? "/admin" : "/dashboard"} /> : <ForgotPassword />} 
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  {isAdmin ? <Navigate to="/admin" /> : <Dashboard />}
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  {isAdmin ? <AdminDashboard /> : <Navigate to="/dashboard" />}
                </ProtectedRoute>
              }
            />
            <Route
              path="/credit-usage"
              element={
                <ProtectedRoute>
                  <CreditUsage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment-success"
              element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment-cancel"
              element={
                <ProtectedRoute>
                  <PaymentCancel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment-complete-desktop"
              element={<PaymentCompleteDesktop />}
            />
            <Route 
              path="/" 
              element={<Navigate to={user ? (isAdmin ? "/admin" : "/dashboard") : "/login"} />} 
            />
          </Routes>
        </Router>
      </div>
    </Elements>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 